import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  LogOut, Users, DollarSign, Ticket, UserCheck, Plus, TrendingUp,
  Mail, Phone, Calendar, Award, Clock, CheckCircle
} from 'lucide-react';
import WeeklyCalendar from '@/components/WeeklyCalendar';
import FinalSchedule from '@/components/FinalSchedule';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [codes, setCodes] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [showEditSessions, setShowEditSessions] = useState(false);
  const [editSessionsForm, setEditSessionsForm] = useState({
    total_sessions: 0,
    remaining_sessions: 0
  });
  
  // Edit client info
  const [showEditClient, setShowEditClient] = useState(false);
  const [editClientForm, setEditClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    coaching_type: 'Groupe'
  });
  
  // Add client form
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [addClientForm, setAddClientForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: ''
  });
  
  // Generate code form
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    client_email: '',
    service_category: 'sport',
    coaching_type: 'private',
    session_pack: '12',
    amount_paid: '',
    payment_method: 'virement',
    notes: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchStats();
    fetchClients();
    fetchCodes();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
        setFilteredClients(data.clients || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredClients(clients);
      return;
    }

    const filtered = clients.filter(client => {
      const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
      const email = client.email.toLowerCase();
      const searchLower = query.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
    setFilteredClients(filtered);
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowClientDetails(true);
  };

  const handleGenerateCodeForClient = (client) => {
    setGenerateForm({
      ...generateForm,
      client_email: client.email,
      service_category: client.service_category || 'sport',
      coaching_type: client.coaching_type || 'private'
    });
    setShowClientDetails(false);
    setShowGenerateDialog(true);
  };

  const fetchCodes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCodes(data.codes || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleGenerateCode = async (e) => {
    e.preventDefault();
    setGeneratingCode(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/codes/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...generateForm,
          amount_paid: parseFloat(generateForm.amount_paid)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur de génération');
      }

      toast.success(`✓ Code généré: ${data.code}`);
      setShowGenerateDialog(false);
      fetchCodes();
      fetchStats();
      
      // Reset form
      setGenerateForm({
        client_email: '',
        service_category: 'sport',
        coaching_type: 'private',
        session_pack: '12',
        amount_paid: '',
        payment_method: 'virement',
        notes: ''
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    navigate('/admin/login');
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setAddingClient(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(addClientForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur de création du client');
      }

      toast.success('Client créé avec succès ! Vous pouvez maintenant lui générer un code d\'activation.');
      setShowAddClientDialog(false);
      
      // Reset form
      setAddClientForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: ''
      });
      
      // Refresh clients list
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAddingClient(false);
    }
  };

  const handleToggleClientStatus = async (client) => {
    const action = client.is_active ? 'désactiver' : 'réactiver';
    const confirmMessage = client.is_active 
      ? `Êtes-vous sûr de vouloir désactiver le compte de ${client.first_name} ${client.last_name} ?\n\nLe client ne pourra plus se connecter ni réserver.`
      : `Réactiver le compte de ${client.first_name} ${client.last_name} ?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = client.is_active ? 'deactivate' : 'activate';
      const response = await fetch(`${API_URL}/api/admin/clients/${client.id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || `Erreur lors de ${action}`);
      }

      const data = await response.json();
      toast.success(data.message);
      
      // Refresh data
      fetchClients();
      fetchStats();
      setShowClientDetails(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResetAllSessions = async () => {
    const confirmMessage = `⚠️ ATTENTION : RESET DE TOUTES LES SÉANCES\n\nCette action va :\n• Remettre TOUS les clients à leur nombre de séances initial\n• Réactiver tous les comptes inactifs\n• Cette action est IRRÉVERSIBLE\n\nÊtes-vous absolument sûr de vouloir continuer ?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation
    const doubleConfirm = window.confirm(`Dernière confirmation :\n\nVous allez réinitialiser les séances de TOUS les clients.\n\nContinuer ?`);
    
    if (!doubleConfirm) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/reset-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors du reset');
      }

      const data = await response.json();
      toast.success(`✅ ${data.message}`);
      
      // Refresh data
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditClient = (client) => {
    setEditClientForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      coaching_type: client.coaching_type || 'group' // Use English values
    });
    setShowClientDetails(false);
    setShowEditClient(true);
  };

  const handleSaveEditClient = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/${selectedClient.id}/edit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editClientForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la modification');
      }

      const data = await response.json();
      toast.success('Informations modifiées avec succès !');
      
      // Refresh data
      fetchClients();
      setShowEditClient(false);
      
      // Update selected client to show new data
      setSelectedClient({ ...selectedClient, ...editClientForm });
      setShowClientDetails(true);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditSessions = (client) => {
    setEditSessionsForm({
      total_sessions: client.total_sessions || 0,
      remaining_sessions: client.remaining_sessions || 0
    });
    setShowClientDetails(false); // Close parent dialog first
    setShowEditSessions(true);
  };

  const handleSaveEditSessions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/clients/${selectedClient.id}/edit-sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editSessionsForm)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de la modification');
      }

      const data = await response.json();
      toast.success(data.message);
      
      // Refresh data
      fetchClients();
      fetchStats();
      setShowEditSessions(false);
      setShowClientDetails(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getStatusBadge = (client) => {
    if (client.is_active) {
      return (
        <Badge className="bg-green-600 hover:bg-green-700 text-white">
          <CheckCircle className="w-3 h-3 mr-1" />
          Actif
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-yellow-600 text-yellow-500">
        <Clock className="w-3 h-3 mr-1" />
        En attente
      </Badge>
    );
  };

  const getCodeStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-600', text: 'Actif' },
      used: { color: 'bg-blue-600', text: 'Utilisé' },
      expired: { color: 'bg-red-600', text: 'Expiré' }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    return (
      <Badge className={`${config.color} hover:${config.color} text-white`}>
        {config.text}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Dashboard Coach</h1>
              <p className="text-zinc-400">Gérez vos clients et votre planning</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400 mb-1">Total Clients</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_clients || 0}</p>
                </div>
                <div className="p-3 bg-blue-600/10 rounded-lg">
                  <Users className="h-6 w-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400 mb-1">Clients Actifs</p>
                  <p className="text-3xl font-bold text-green-500">{stats?.active_clients || 0}</p>
                </div>
                <div className="p-3 bg-green-600/10 rounded-lg">
                  <UserCheck className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400 mb-1">Codes Générés</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_codes_generated || 0}</p>
                  <p className="text-xs text-zinc-500 mt-1">{stats?.codes_pending || 0} en attente</p>
                </div>
                <div className="p-3 bg-purple-600/10 rounded-lg">
                  <Ticket className="h-6 w-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-950/30 to-zinc-900/50 border-red-900/50 hover:border-red-800/50 transition-colors">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400 mb-1">Revenu Total</p>
                  <p className="text-3xl font-bold text-red-500">{stats?.total_revenue?.toFixed(0) || 0}€</p>
                </div>
                <div className="p-3 bg-red-600/10 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Migration Button (if needed) */}
        <Card className="bg-yellow-950/20 border-yellow-900/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-yellow-300 mb-1">🔧 Migration Base de Données</p>
                <p className="text-xs text-zinc-400">Si certaines réservations ne s'affichent pas, cliquez sur "Migrer"</p>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    console.log('🔄 Calling migration endpoint...');
                    
                    const response = await fetch(`${API_URL}/api/admin/migrate-bookings`, {
                      method: 'POST',
                      headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    console.log('Response status:', response.status);
                    
                    if (!response.ok) {
                      const errorText = await response.text();
                      console.error('Error response:', errorText);
                      toast.error(`Erreur ${response.status}: ${errorText}`);
                      return;
                    }
                    
                    const data = await response.json();
                    console.log('Migration result:', data);
                    
                    toast.success(data.message || 'Migration réussie !');
                    
                    // Reload page to show migrated bookings
                    setTimeout(() => window.location.reload(), 1500);
                    
                  } catch (error) {
                    console.error('Migration error:', error);
                    toast.error('Erreur : ' + error.message);
                  }
                }}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                Migrer
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="clients" className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
            <TabsTrigger value="clients" className="data-[state=active]:bg-red-600">
              <Users className="w-4 h-4 mr-2" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="codes" className="data-[state=active]:bg-red-600">
              <Ticket className="w-4 h-4 mr-2" />
              Codes
            </TabsTrigger>
            <TabsTrigger value="schedule" className="data-[state=active]:bg-red-600">
              <Calendar className="w-4 h-4 mr-2" />
              Réservations
            </TabsTrigger>
            <TabsTrigger value="final" className="data-[state=active]:bg-red-600">
              <Calendar className="w-4 h-4 mr-2" />
              Planning Final
            </TabsTrigger>
          </TabsList>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <CardTitle className="text-white text-xl">Mes Clients</CardTitle>
                    <CardDescription className="text-zinc-400">
                      {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} 
                      {searchQuery && ` (${clients.length} au total)`}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleResetAllSessions}
                      variant="outline"
                      className="border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
                    >
                      🔄 Reset Séances
                    </Button>
                    <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-red-600 hover:bg-red-700">
                          <Plus className="mr-2 h-4 w-4" />
                          Ajouter un Client
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-xl">Ajouter un Nouveau Client</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                          Créez un compte client. Vous pourrez ensuite lui générer un code d'activation.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddClient} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Prénom *</Label>
                            <Input
                              type="text"
                              placeholder="Jean"
                              value={addClientForm.first_name}
                              onChange={(e) => setAddClientForm({...addClientForm, first_name: e.target.value})}
                              required
                              className="bg-zinc-800 border-zinc-700 text-white"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Nom *</Label>
                            <Input
                              type="text"
                              placeholder="Dupont"
                              value={addClientForm.last_name}
                              onChange={(e) => setAddClientForm({...addClientForm, last_name: e.target.value})}
                              required
                              className="bg-zinc-800 border-zinc-700 text-white"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-zinc-300">Email *</Label>
                          <Input
                            type="email"
                            placeholder="client@example.com"
                            value={addClientForm.email}
                            onChange={(e) => setAddClientForm({...addClientForm, email: e.target.value})}
                            required
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-zinc-300">Téléphone *</Label>
                          <Input
                            type="tel"
                            placeholder="0489 12 34 56"
                            value={addClientForm.phone}
                            onChange={(e) => setAddClientForm({...addClientForm, phone: e.target.value})}
                            required
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-zinc-300">Mot de passe *</Label>
                          <Input
                            type="password"
                            placeholder="Minimum 6 caractères"
                            value={addClientForm.password}
                            onChange={(e) => setAddClientForm({...addClientForm, password: e.target.value})}
                            required
                            minLength={6}
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                          <p className="text-xs text-zinc-500">Le client utilisera ce mot de passe pour se connecter</p>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowAddClientDialog(false)}
                            className="flex-1 border-zinc-700"
                          >
                            Annuler
                          </Button>
                          <Button
                            type="submit"
                            disabled={addingClient}
                            className="flex-1 bg-red-600 hover:bg-red-700"
                          >
                            {addingClient ? 'Création...' : 'Créer le Client'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="🔍 Rechercher un client (nom, prénom, email)..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white pl-4 pr-10"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => handleSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {filteredClients.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500">
                      {searchQuery ? 'Aucun client trouvé' : 'Aucun client pour le moment'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredClients.map(client => (
                      <div
                        key={client.id}
                        onClick={() => handleClientClick(client)}
                        className="bg-zinc-800/50 border border-zinc-800 rounded-lg p-4 hover:border-red-600 hover:bg-zinc-800 transition-all cursor-pointer"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-semibold text-lg">
                                {client.first_name} {client.last_name}
                              </h3>
                              {getStatusBadge(client)}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                              <div className="flex items-center gap-2 text-zinc-400">
                                <Mail className="w-4 h-4" />
                                {client.email}
                              </div>
                              <div className="flex items-center gap-2 text-zinc-400">
                                <Phone className="w-4 h-4" />
                                {client.phone}
                              </div>
                            </div>
                          </div>

                          {client.is_active && (
                            <div className="text-right ml-4">
                              <div className="bg-zinc-900 rounded-lg px-4 py-2 border border-zinc-700">
                                <div className="flex items-center gap-2 mb-1">
                                  <Award className="w-4 h-4 text-red-500" />
                                  <span className="text-xs text-zinc-400">Séances</span>
                                </div>
                                <p className="text-2xl font-bold text-white">
                                  {client.remaining_sessions}<span className="text-zinc-600">/{client.total_sessions}</span>
                                </p>
                                <div className="flex gap-2 mt-2 text-xs">
                                  <Badge variant="outline" className="border-zinc-600 text-zinc-400 capitalize">
                                    {client.service_category}
                                  </Badge>
                                  <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                                    {client.coaching_type === 'private' ? 'Privé' : 'Groupe'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 text-xs text-zinc-500 flex items-center gap-2">
                          👆 Cliquer pour voir les détails et générer un code
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-white text-xl">Codes d'Activation</CardTitle>
                    <CardDescription className="text-zinc-400">
                      Générez et gérez les codes pour vos clients
                    </CardDescription>
                  </div>
                  <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-red-600 hover:bg-red-700">
                        <Plus className="mr-2 h-4 w-4" />
                        Nouveau Code
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-xl">Générer un Code d'Activation</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                          Créez un code pour activer le pack d'un client
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleGenerateCode} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-300">Email du Client *</Label>
                          <Input
                            type="email"
                            placeholder="client@example.com"
                            value={generateForm.client_email}
                            onChange={(e) => setGenerateForm({...generateForm, client_email: e.target.value})}
                            required
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                          <p className="text-xs text-zinc-500">Le client doit être déjà inscrit</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Catégorie *</Label>
                            <Select
                              value={generateForm.service_category}
                              onValueChange={(value) => setGenerateForm({...generateForm, service_category: value})}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="sport">Sport</SelectItem>
                                <SelectItem value="duo">Duo</SelectItem>
                                <SelectItem value="nutrition">Nutrition</SelectItem>
                                <SelectItem value="football">Football</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-zinc-300">Type de Coaching *</Label>
                            <Select
                              value={generateForm.coaching_type}
                              onValueChange={(value) => setGenerateForm({...generateForm, coaching_type: value})}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="private">Privé (1 personne)</SelectItem>
                                <SelectItem value="group">Groupe (4 personnes)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-zinc-300">Pack de Séances *</Label>
                            <Select
                              value={generateForm.session_pack}
                              onValueChange={(value) => setGenerateForm({...generateForm, session_pack: value})}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="8">8 séances (2/semaine)</SelectItem>
                                <SelectItem value="12">12 séances (3/semaine)</SelectItem>
                                <SelectItem value="16">16 séances (4/semaine)</SelectItem>
                                <SelectItem value="20">20 séances (5/semaine)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-zinc-300">Montant Payé (€) *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="350.00"
                              value={generateForm.amount_paid}
                              onChange={(e) => setGenerateForm({...generateForm, amount_paid: e.target.value})}
                              required
                              className="bg-zinc-800 border-zinc-700 text-white"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-zinc-300">Notes (optionnel)</Label>
                          <Input
                            placeholder="Ex: Pack découverte, client recommandé..."
                            value={generateForm.notes}
                            onChange={(e) => setGenerateForm({...generateForm, notes: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white"
                          />
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowGenerateDialog(false)}
                            className="flex-1 border-zinc-700"
                          >
                            Annuler
                          </Button>
                          <Button 
                            type="submit" 
                            disabled={generatingCode}
                            className="flex-1 bg-red-600 hover:bg-red-700"
                          >
                            {generatingCode ? 'Génération...' : 'Générer le Code'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {codes.length === 0 ? (
                  <div className="text-center py-12">
                    <Ticket className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 mb-4">Aucun code généré</p>
                    <Button 
                      onClick={() => setShowGenerateDialog(true)}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Créer le premier code
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {codes.map(code => (
                      <div
                        key={code.id}
                        className="bg-zinc-800/50 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <code className="text-lg font-mono font-bold text-red-500 bg-zinc-900 px-3 py-1 rounded border border-zinc-700">
                                {code.code}
                              </code>
                              {getCodeStatusBadge(code.status)}
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="text-zinc-300">
                                <Mail className="w-3 h-3 inline mr-2" />
                                {code.client_email}
                              </p>
                              <div className="flex gap-4 text-zinc-400">
                                <span>📦 {code.total_sessions} séances</span>
                                <span>💰 {code.amount_paid}€</span>
                                <span className="capitalize">🏋️ {code.service_category}</span>
                              </div>
                              {code.notes && (
                                <p className="text-xs text-zinc-500 italic">"{code.notes}"</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <WeeklyCalendar token={localStorage.getItem('adminToken')} />
          </TabsContent>

          {/* Final Schedule Tab */}
          <TabsContent value="final">
            <FinalSchedule token={localStorage.getItem('adminToken')} />
          </TabsContent>
        </Tabs>

        {/* Client Details Dialog */}
        <Dialog open={showClientDetails} onOpenChange={setShowClientDetails}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">
                {selectedClient?.first_name} {selectedClient?.last_name}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Fiche client complète
              </DialogDescription>
            </DialogHeader>

            {selectedClient && (
              <div className="space-y-6">
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  {getStatusBadge(selectedClient)}
                  {selectedClient.remaining_sessions === 0 && (
                    <Badge className="bg-orange-600">Renouvellement nécessaire</Badge>
                  )}
                </div>

                {/* Contact Info */}
                <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-white mb-3">Informations de contact</h3>
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-300">{selectedClient.email}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-zinc-500" />
                      <span className="text-zinc-300">{selectedClient.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Edit Button */}
                <Button
                  onClick={() => handleEditClient(selectedClient)}
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  ✏️ Modifier les informations
                </Button>

                {/* Pack Info */}
                {selectedClient.is_active && (
                  <div className="bg-zinc-800 rounded-lg p-4">
                    <h3 className="font-semibold text-white mb-3">Pack actuel</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Catégorie</p>
                        <p className="text-white capitalize">{selectedClient.service_category}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Type</p>
                        <p className="text-white">
                          {selectedClient.coaching_type === 'private' ? 'Privé' : 'Groupe'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Total séances</p>
                        <p className="text-white text-xl font-bold">{selectedClient.total_sessions}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Restantes</p>
                        <p className={`text-xl font-bold ${
                          selectedClient.remaining_sessions === 0 ? 'text-red-500' :
                          selectedClient.remaining_sessions <= 2 ? 'text-orange-500' : 'text-green-500'
                        }`}>
                          {selectedClient.remaining_sessions}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="pt-4 border-t border-zinc-800 space-y-3">
                  <Button
                    onClick={() => handleGenerateCodeForClient(selectedClient)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Plus className="mr-2 h-5 w-5" />
                    {selectedClient.remaining_sessions === 0 
                      ? 'Générer un code de renouvellement'
                      : 'Générer un nouveau code'}
                  </Button>
                  <p className="text-xs text-zinc-500 text-center">
                    Le client recevra un code d'activation pour un nouveau pack
                  </p>

                  {/* Deactivate/Activate Button */}
                  <Button
                    onClick={() => handleToggleClientStatus(selectedClient)}
                    variant="outline"
                    className={`w-full ${
                      selectedClient.is_active 
                        ? 'border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white' 
                        : 'border-green-600 text-green-600 hover:bg-green-600 hover:text-white'
                    }`}
                  >
                    {selectedClient.is_active ? '🔴 Désactiver ce compte' : '🟢 Réactiver ce compte'}
                  </Button>
                  <p className="text-xs text-zinc-500 text-center">
                    {selectedClient.is_active 
                      ? 'Le client ne pourra plus se connecter ni réserver'
                      : 'Le client pourra à nouveau se connecter et réserver'}
                  </p>

                  {/* Edit Sessions Button */}
                  <Button
                    onClick={() => handleEditSessions(selectedClient)}
                    variant="outline"
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                  >
                    ✏️ Modifier les séances
                  </Button>
                  <p className="text-xs text-zinc-500 text-center">
                    Corriger manuellement le nombre de séances en cas de bug
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Client Info Dialog */}
        <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Modifier les informations</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {selectedClient && `${selectedClient.first_name} ${selectedClient.last_name}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-zinc-400 block mb-2">Prénom</label>
                  <Input
                    value={editClientForm.first_name}
                    onChange={(e) => setEditClientForm({...editClientForm, first_name: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-2">Nom</label>
                  <Input
                    value={editClientForm.last_name}
                    onChange={(e) => setEditClientForm({...editClientForm, last_name: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-2">Email</label>
                <Input
                  type="email"
                  value={editClientForm.email}
                  onChange={(e) => setEditClientForm({...editClientForm, email: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-2">Téléphone</label>
                <Input
                  value={editClientForm.phone}
                  onChange={(e) => setEditClientForm({...editClientForm, phone: e.target.value})}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-2">Type de coaching</label>
                <select
                  value={editClientForm.coaching_type}
                  onChange={(e) => setEditClientForm({...editClientForm, coaching_type: e.target.value})}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                >
                  <option value="group">Groupe</option>
                  <option value="private">Privé</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowEditClient(false)}
                  className="flex-1 border-zinc-700 text-zinc-300"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveEditClient}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Sessions Dialog */}
        <Dialog open={showEditSessions} onOpenChange={setShowEditSessions}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
            <DialogHeader>
              <DialogTitle>Modifier les séances</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {selectedClient && `${selectedClient.first_name} ${selectedClient.last_name}`}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-zinc-300 mb-2 block">
                  Total de séances
                </label>
                <input
                  type="number"
                  min="0"
                  value={editSessionsForm.total_sessions}
                  onChange={(e) => setEditSessionsForm({
                    ...editSessionsForm,
                    total_sessions: parseInt(e.target.value) || 0
                  })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white"
                />
              </div>

              <div>
                <label className="text-sm text-zinc-300 mb-2 block">
                  Séances restantes
                </label>
                <input
                  type="number"
                  min="0"
                  max={editSessionsForm.total_sessions}
                  value={editSessionsForm.remaining_sessions}
                  onChange={(e) => setEditSessionsForm({
                    ...editSessionsForm,
                    remaining_sessions: parseInt(e.target.value) || 0
                  })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Ne peut pas dépasser le total de séances
                </p>
              </div>

              <div className="bg-blue-950 border border-blue-900 rounded p-3">
                <p className="text-xs text-blue-300">
                  💡 <strong>Info :</strong> Les séances utilisées seront calculées automatiquement
                  <br />
                  <span className="text-blue-400">
                    Utilisées = {editSessionsForm.total_sessions - editSessionsForm.remaining_sessions}
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEditSessions(false)}
                  className="flex-1 border-zinc-700"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveEditSessions}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Sauvegarder
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
